import { createSelector } from 'reselect';

import { getVariantType } from 'helpers/utils/variantType';
import { RemoteConfig } from 'store/remoteConfig/types';
import { AppState } from 'store/app/types';
import { ProductState, Variant, SizeData } from './types';

const productPage = (state: { productPage: ProductState }) => state.productPage;

export const selectCurrentSku = createSelector(
  [productPage],
  (productData): Variant | undefined => {
    const { product } = productData;
    if (product) {
      const { selectedVariantId, variants } = product;
      if (selectedVariantId && variants && variants.length > 0) {
        let selectedVariantIndex = variants.findIndex(
          (variant) => variant.childId === selectedVariantId
        );
        if (selectedVariantIndex < 0) {
          selectedVariantIndex = 0;
        }
        if (product?.siblings) {
          variants[selectedVariantIndex].siblings = product?.siblings;
        }
        return variants[selectedVariantIndex];
      } else {
        return {
          buttonText: product.buttonText,
          childId: product.id,
          discount: product.discount,
          isBestSeller: false,
          inStock: product.inStock,
          media: product.parentMedia,
          mrp: product.mrp,
          name: product.name,
          offerPrice: product.offerPrice,
          productAttributes: product.productAttributes,
          packSize: product.packSize,
          isMini: product.isMini || false,
          deliveryData: product.deliveryData,
          quantity: product.quantity,
          variantName: product.name,
          sku: product.sku,
          slug: product.slug,
          expiry: product.expiry,
          returnAvailable: product.returnAvailable,
          messageOnReturn: product.messageOnReturn,
          featured: product.featured,
          productType: product.productType,
          onlyWishlistButton: product.onlyWishlistButton,
          imageUrl: product.imageUrl,
          manufacture: product.manufacture,
          isLux: product.isLux,
          contentLang: product?.contentLang || {},
          defaultPid: product?.defaultPid,
          siblings: product?.siblings,
          prData: product?.prData,
          isEarlyAccess: product?.isEarlyAccess,
          isFlashSale: product?.isFlashSale,
          isPrEnabled: product?.isPrEnabled,
          ratingCount: product?.ratingCount,
          reviewCount: product?.reviewCount,
          rating: product?.rating,
          cohortSaleTemplate: product?.cohortSaleTemplate,
          cohortSaleTemplateV2: product?.cohortSaleTemplateV2,
          multiMrp: product?.multiMrp,
          productTitle: product?.productTitle,
          urgencyTemplate: product?.urgencyTemplate,
          social_proof_nudges: product?.social_proof_nudges,
          bestSellerTagInfo: product?.bestSellerTagInfo,
          primaryTags: product?.primaryTags,
          secondaryTag: product?.secondaryTag,
          leftTag: product?.leftTag,
          primaryTagsNames: product?.primaryTagsNames || [],
          leftTagName: product?.leftTagName || '',
          secondaryTagName: product?.secondaryTagName || '',
          earlyAccessData: product?.earlyAccessData || {},
          secondaryTagV2: product?.secondaryTagV2,
        };
      }
    }
  }
);

export const selectCurrentProductId = createSelector(
  [productPage],
  (productData): string | undefined => {
    const { product } = productData || {};

    if (product) {
      return product.id;
    }
  }
);

export const selectCurrentProductType = createSelector(
  [productPage],
  (productData): string | undefined => {
    const { product } = productData || {};

    if (product) {
      return product?.productType || '';
    }
  }
);

export const selectIsProductLoading = createSelector(
  [productPage],
  (productData) => productData.loading
);

export const selectIsFetchingError = createSelector(
  [productPage],
  (productData) => productData.isFetchingError
);

export const selectRatingInfo = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { rating, ratingCount, reviewCount, formattedRatingCount } = product;

    return {
      rating,
      ratingCount,
      reviewCount,
      formattedRatingCount,
    };
  }
});

export const selectPriceInfo = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { discount, mrp, offerPrice } = product;

    return {
      discount,
      mrp,
      offerPrice,
    };
  }
});

export const selectOffers = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { offers = [] } = product;

    return offers;
  }
});

export const selectCoupons = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { coupons = [] } = product;

    return coupons;
  }
});

export const selectBestOfferDetails = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { bestOfferDetail = {} } = product;

      return bestOfferDetail;
    }
  }
);

export const selectPersonalizedBestOfferDetails = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { personalizedBestOfferDetail = {} } = product;

      return personalizedBestOfferDetail;
    }
  }
);

export const selectFreeGiftOffers = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { freeGiftOffers = [] } = product;

      return freeGiftOffers;
    }
  }
);

const isPdpCustomVideo = (state: {
  remoteConfig: RemoteConfig;
  appReducer: AppState;
}) => {
  const { status } = state.remoteConfig.configs?.AB?.pdpCustomVideo || {};
  return status;
};

export const selectProductMedia = createSelector(
  [productPage, isPdpCustomVideo],
  (productData, pdpCustomVideoEnabled) => {
    const { product } = productData;
    if (product) {
      const { selectedVariantId, variants } = product;

      if (selectedVariantId && variants && variants.length > 0) {
        let selectedVariantIndex = variants.findIndex(
          (variant) => variant.childId === selectedVariantId
        );
        if (selectedVariantIndex < 0) {
          selectedVariantIndex = 0;
        }
        if (
          pdpCustomVideoEnabled &&
          variants[selectedVariantIndex]?.carousel?.length > 0
        ) {
          return variants[selectedVariantIndex]?.carousel;
        }
        return variants[selectedVariantIndex]?.media;
      } else {
        if (pdpCustomVideoEnabled && product.parentCarousel?.length > 0) {
          return product.parentCarousel;
        }
        return product.parentMedia;
      }
    }
  }
);

export const selectProductRelatedVideo = createSelector(
  [productPage, isPdpCustomVideo],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { selectedVariantId, variants } = product;

      if (selectedVariantId && variants && variants.length > 0) {
        let selectedVariantIndex = variants.findIndex(
          (variant) => variant.childId === selectedVariantId
        );
        if (selectedVariantIndex < 0) {
          selectedVariantIndex = 0;
        }
        return variants[selectedVariantIndex]?.relatedVideo;
      } else {
        return product.parentRelatedVideo;
      }
    }
  }
);

export const selectVariants = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { variants } = product;

    if (variants && variants.length > 0) {
      return variants;
    }
  }
});

export const selectVariantType = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { productType, variantType } = product;

      if (productType === 'simple') {
        return;
      }

      if (productType === 'configurable') {
        return getVariantType(variantType);
      }
    }
  }
);

export const selectBrandAuth = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { brandAuth } = product;

    return brandAuth;
  }
});

export const selectBrandName = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { brandName } = product;

    return brandName;
  }
});

interface SizeChartOption {
  id: string;
  packSize: string;
  sizeData: SizeData;
  inStock: boolean;
}

export const selectSizeChartVariants = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    const transformedVariants: SizeChartOption[] = [];
    if (product) {
      const { variants } = product;
      if (Array.isArray(variants)) {
        variants?.forEach((variant) => {
          transformedVariants.push({
            packSize: variant?.packSize || '',
            id: variant.childId,
            sizeData: variant.sizeData || {},
            inStock: variant.inStock,
          });
        });
      }
    }
    return transformedVariants;
  }
);

export const selectBundleChildIds = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    const childSkus = product?.bundleChildSkus || {};
    const transformedChildSkus = Object.keys(childSkus) || [];
    return transformedChildSkus;
  }
);
